/* eslint-disable no-unsafe-optional-chaining */
import i18n from '@_plugins/i18n';
import { container } from '@_plugins/ioc';
import LaravelAPI from '@data/api/laravel.api';
import { toSortParams } from '@data/dto/api.dto';
import { toStrategicAxis } from '@data/dto/strategic-axis.dto';
import { Meta, PaginateResult, SortParams } from '@data/models/api.interface';
import { DatatableReorderParams } from '@data/models/datatable.interface';
import { FilterParams } from '@data/models/filter.interface';
import {
  StrategicAxis,
  StrategicAxisHttp,
  StrategicAxisWizard,
} from '@data/models/strategic-axis.interface';
import ErrorService from 'core/error.service';
import { injectable } from 'inversify';

@injectable()
export default class StrategicAxisService {
  private API = container.get<LaravelAPI>(LaravelAPI);
  private readonly strategicAxisEndpoint = '/strategic-axis-repositories';
  private errorService = container.get<ErrorService>(ErrorService);

  public async listStrategicAxis(
    filters: FilterParams,
  ): Promise<PaginateResult<StrategicAxis[]>> {
    const { data: response, error } = await this.API?.get<{
      data: StrategicAxisHttp[];
      meta: Meta;
    }>(this.strategicAxisEndpoint, filters);

    if (error) {
      this.errorService.handleHttpError(error);
      return {
        items: [],
        totalSize: 0,
      };
    }

    return {
      items: response?.data?.map(toStrategicAxis) || [],
      totalSize: response?.meta.page.total || 0,
    };
  }

  public async upsertStrategicAxis({
    id,
    ...strategicAxisWizard
  }: StrategicAxisWizard): Promise<StrategicAxis | void> {
    if (!id) {
      const { data, error } = await this.API?.post<
        { data: Omit<StrategicAxisWizard, 'id'> },
        { data: StrategicAxisHttp }
      >(this.strategicAxisEndpoint, { data: strategicAxisWizard });

      if (error) {
        this.errorService.handleHttpError(error);
        return;
      }

      return toStrategicAxis(data?.data || {});
    }

    const { data, error } = await this.API?.patch<
      { data: StrategicAxisWizard },
      { data: StrategicAxisHttp }
    >(`${this.strategicAxisEndpoint}/${id}`, {
      data: { ...strategicAxisWizard, id },
    });

    if (error) {
      this.errorService.handleHttpError(error);
      return;
    }

    return toStrategicAxis(data?.data || {});
  }

  public async deleteStrategicAxis(id: string): Promise<StrategicAxis | void> {
    if (!id) {
      this.errorService.handleError({
        title: i18n.t('error.deleting'),
        message: i18n.t('error.id.missing'),
      });
      return;
    }

    const { data, error } = await this.API?.delete<{
      data: StrategicAxisHttp;
    }>(`${this.strategicAxisEndpoint}/${id}`);

    if (error) {
      this.errorService.handleHttpError(error);
      return;
    }

    return toStrategicAxis(data?.data || {});
  }

  public async sortStrategicAxis(
    sortParams: DatatableReorderParams<StrategicAxis>,
  ): Promise<StrategicAxis | void> {
    const {
      current: { id },
    } = sortParams;

    if (!id) {
      this.errorService.handleError({
        title: i18n.t('error.sorting'),
        message: i18n.t('error.id.missing'),
      });
      return;
    }

    const { error } = await this.API?.post<{ data: SortParams }, {}>(
      `${this.strategicAxisEndpoint}/${id}/sort`,
      {
        data: toSortParams('strategic_axis_repositories', sortParams),
      },
    );

    if (error) {
      this.errorService.handleHttpError(error);
      return;
    }

    return sortParams.current;
  }
}
